<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import ProgressFrame from './layout/ProgressFrame.vue';
import ButtonPrimary from '@/shared/components/button/ButtonPrimary.vue';
import HorizontalContentRow from '../components/HorizontalContentRow.vue';
import AllocationsPerPhase from '../components/allocations/AllocationsPerPhase.vue';
import { useCommissioningDeviceStore } from '../stores/commissioningDeviceStore';
import { useDeviceSpecificationStore } from '../stores/deviceSpecificationStore';
import {
  generateInitAllocations,
  sumAllocations,
  getKwpAllocationPerPhase,
  ALLOCATION_UNIT,
} from '../utils/allocations';
import type {
  TerminalAllocation,
  AllocPhaseIndex,
  AllocGroupIndex,
} from '../models/SolshareSystemSpec';
import { onMounted, ref, watch, computed } from 'vue';
import router from '@/router';
import { ALGORITHM_TYPE, COUNTRY_CODE } from '@/utils/constants';
import { message, type FormInstance } from 'ant-design-vue';
import { KWP_ALLOC_COUNTRIES } from '../constants/config-constants';

const { t } = useI18n();

const commissioningDeviceStore = useCommissioningDeviceStore();
const serialNumber = commissioningDeviceStore.serialNumber;

const deviceSpecificationStore = useDeviceSpecificationStore();

const baseKwp = deviceSpecificationStore.solshareSystemSpec.inverterSpec.systemSize ?? 0;
const kwpPerPhase = getKwpAllocationPerPhase(serialNumber, baseKwp);
const showAsKwp = KWP_ALLOC_COUNTRIES.includes(
  deviceSpecificationStore.solshareSite?.country as COUNTRY_CODE,
);
const allocationUnitSymbol = showAsKwp
  ? ' kWp' // Add space before the value, e.g. 4.30 kWp
  : '%'; // No space before the value, e.g. 93.30%

const formRef = ref<FormInstance>();
const formData = ref<TerminalAllocation>({} as TerminalAllocation);
const algorithmType = computed({
  get: () => deviceSpecificationStore.draft.algorithmType || ALGORITHM_TYPE.CUSTOM_ALLOCATION,
  set: (value: ALGORITHM_TYPE) => deviceSpecificationStore.updateDraftAlgorithmType(value),
});

onMounted(() => {
  const deviceConnections = deviceSpecificationStore.solshareSystemSpec.connections;
  if (!deviceConnections || deviceConnections.length === 0) {
    router.push({ name: 'ConfigureSolshareConfirmSite' });
  }

  // Algorithm type
  if (!deviceSpecificationStore.draft.algorithmType) {
    deviceSpecificationStore.updateDraftAlgorithmType(ALGORITHM_TYPE.CUSTOM_ALLOCATION);
  }

  let allocations = {} as TerminalAllocation;
  if (!deviceSpecificationStore.hasDraftAllocations) {
    const rawDetectedConnections = commissioningDeviceStore.outputConnections;

    // The new allocation unit is configured based on the country
    const newAllocationUnit = showAsKwp ? ALLOCATION_UNIT.kWp : ALLOCATION_UNIT.percentage;

    // Generate the initial allocations
    allocations = generateInitAllocations(
      deviceConnections,
      rawDetectedConnections,
      newAllocationUnit,
      kwpPerPhase,
    );

    // Update the allocation unit in the store
    deviceSpecificationStore.updateDraftAllocationUnit(newAllocationUnit);
  } else {
    allocations = deviceSpecificationStore.draft.allocations;
  }

  formData.value = allocations;
});

watch(
  formData,
  (newVal: TerminalAllocation) => {
    deviceSpecificationStore.updateDraftTerminalAllocation(newVal);
  },
  { deep: true },
);

const algorithmTypeOptions = [
  {
    label: 'Custom allocation',
    value: ALGORITHM_TYPE.CUSTOM_ALLOCATION,
    description:
      'Allocates solar each calendar month according to a pre-defined allocation arrangement. Consult solar system designs or SLDs.',
  },
  {
    label: 'Demand-based',
    value: ALGORITHM_TYPE.DEMAND_BASED_DELIVERY,
    description:
      'Allocates solar according to real-time demand only. No allocation arrangements are taken into account.',
  },
];

const updateAllocationValue = (
  phaseIndex: AllocPhaseIndex,
  groupIndex: AllocGroupIndex,
  value: number,
) => {
  const phase = formData.value[phaseIndex];
  if (phase && phase[groupIndex]) {
    phase[groupIndex].allocationValue = value;
  }
};

const validAllocations = (allocations: TerminalAllocation) => {
  if (algorithmType.value === ALGORITHM_TYPE.CUSTOM_ALLOCATION) {
    const sums = sumAllocations(allocations);
    return sums.every((sum: number) => {
      if (showAsKwp) {
        return sum === kwpPerPhase;
      } else {
        return sum === 100;
      }
    });
  }

  return true;
};

const confirmAllocations = async () => {
  const isValid = validAllocations(formData.value);

  if (isValid) {
    commissioningDeviceStore.isUploadConfig = true;
    router.push({ name: 'StartCommissioning' });
  } else {
    message.error(`Allocation remaining must be 0${allocationUnitSymbol} for each phase.`);
  }
};
</script>

<template>
  <ProgressFrame pageClass="subsection-page" withBackArrow titleTight subTitleColor="secondary">
    <template #title>{{ t('commissioning.configureAllocations.pageTitle') }}</template>
    <template #subTitle>{{ serialNumber }}</template>

    <div class="algorithm-type-container">
      <div class="algorithm-type-dropdown">
        <a-select
          v-model:value="algorithmType"
          :options="algorithmTypeOptions"
          size="large"
          data-tn="algorithm-type-select"
        >
          <a-select-option
            v-for="option in algorithmTypeOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </a-select-option>
        </a-select>
      </div>

      <p class="algorithm-type-description">
        {{ algorithmTypeOptions.find((option) => option.value === algorithmType)?.description }}
      </p>
    </div>

    <a-form ref="formRef" :model="formData">
      <template v-for="(_, phaseIndexKey) in formData" :key="`L${phaseIndexKey}`">
        <HorizontalContentRow
          :firstRow="Number(phaseIndexKey) === 0"
          :lastRow="Number(phaseIndexKey) === 2"
          noSidePadding
        >
          <AllocationsPerPhase
            :phaseIndex="Number(phaseIndexKey) as AllocPhaseIndex"
            :phaseAllocations="formData[phaseIndexKey as AllocPhaseIndex]"
            :algorithmType="algorithmType"
            @updateAllocationValue="updateAllocationValue"
            :showAsKwp="showAsKwp"
            :kwpPerPhase="kwpPerPhase"
          />
        </HorizontalContentRow>
      </template>
    </a-form>

    <div class="end-of-page">
      <ButtonPrimary block @click="confirmAllocations" data-tn="confirm-btn">
        Confirm
      </ButtonPrimary>
    </div>
  </ProgressFrame>
</template>

<style lang="less" scoped>
.algorithm-type-container {
  :deep(.ant-select) {
    color: @color-primary;
    background-color: white;
    width: 100%;
  }

  margin: @content-side-margin;
  margin-bottom: @content-side-margin;

  .algorithm-type-dropdown {
    margin-bottom: 0.5rem;
  }

  .algorithm-type-description {
    font-size: 0.85rem;
  }
}

.end-of-page {
  margin: @content-side-margin;
}
</style>
